<template>
  <div class="row">
    <div v-if="showPalette" class="col-sm-2">
      <div class="edition-panel">
        <draw-button-palette
          :can-add-shapes="canAddMoreShapes"
          :draw-mode="drawMode"
          :drawing-modes="drawingModes"
          :outline="true"
          :btn-block="true"
          :show-undo="!!lastShape"
          :show-finish="drawMode === 'polygon'"
          :disable-finish="draftPointCount < 3"
          :show-undo-point="drawMode === 'polygon' && draftPointCount > 0"
          :show-cancel="drawMode === 'polygon'"
          :show-delete="shapeLimit > 1"
          :disable-delete="!selectedShape"
          @mode="setMode"
          @finish="finishDrawing"
          @undoPoint="undoDraftPoint"
          @cancel="cancelDraft"
          @undo="undo"
          @deleteSelected="deleteSelectedShape"
          @deleteAll="deleteShapes"
        >
        </draw-button-palette>

        <div class="row color-palette mt-3">
          <div v-for="(color, key) in colors" :key="key" class="col-sm-3">
            <div
              class="color-button mb-2"
              :class="{ active: selectedColor === color }"
              :style="{ 'background-color': color, border: `2px solid ${color}` }"
              @click="useColor(color)"
            ></div>
          </div>
        </div>
      </div>
    </div>
    <div :class="{ 'col-sm-10': showPalette, 'col-sm-12': !showPalette }">
      <div class="map-wrapper">
        <div :id="mapId" class="map-canvas"></div>
        <div v-if="showFloatButtons" class="floating-panel">
          <draw-button-palette
            :can-add-shapes="canAddMoreShapes"
            :draw-mode="drawMode"
            :drawing-modes="drawingModes"
            :show-undo="!!lastShape"
            :show-finish="drawMode === 'polygon'"
            :disable-finish="draftPointCount < 3"
            :show-undo-point="drawMode === 'polygon' && draftPointCount > 0"
            :show-cancel="drawMode === 'polygon'"
            :show-delete="shapeLimit > 1"
            :disable-delete="!selectedShape"
            @mode="setMode"
            @finish="finishDrawing"
            @undoPoint="undoDraftPoint"
            @cancel="cancelDraft"
            @undo="undo"
            @deleteSelected="deleteSelectedShape"
            @deleteAll="deleteShapes"
          >
          </draw-button-palette>
        </div>
        <div v-if="drawMode === 'polygon'" class="drawing-status" aria-live="polite">
          <strong>Delimitando lote</strong>
          <span>Puntos: {{ draftPointCount }} · mínimo 3 para cerrar</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import initMapMixin from '../../../mixins/initMapMixin';
import drawingMapMixin from '../../../mixins/drawingMapMixin';
import DrawButtonPalette from './DrawButtonPalette';

export default {
  components: {
    DrawButtonPalette,
  },

  mixins: [initMapMixin, drawingMapMixin],
  props: {
    defaultCoordinates: {
      type: Array,
      default: null,
    },

    showPalette: {
      type: Boolean,
      default: true,
    },

    showFloatButtons: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      drawingModes: ['polygon'],
      defaultMarker: null,
      drawMode: null,
      draftPolygon: null,
      draftLine: null,
      draftMarkers: [],
      draftPointCount: 0,
      pathListeners: [],
    };
  },

  computed: {
    canAddMoreShapes() {
      return !this.draftPolygon && this.shapes.length < this.shapeLimit;
    },
  },

  beforeDestroy() {
    this.pathListeners.forEach((listener) => listener.remove());
  },

  methods: {
    mapLoaded() {
      this.google.maps.event.addListener(this.map, 'click', (event) => {
        if (this.drawMode === 'polygon') {
          this.addDraftPoint(event);
          return;
        }

        this.clearSelection();
      });
      this.selectColor(this.colors[0]);
      this.setDefaultCoordinates();
    },

    setMode(mode) {
      if (mode === null) {
        this.cancelDraft();
        return;
      }

      if (mode !== 'polygon' || this.drawMode === mode || !this.canAddMoreShapes) {
        return;
      }

      this.drawMode = mode;
      this.clearSelection();
      // Google Maps no dispara un click luego de un arrastre: tocar agrega
      // un vértice y mantener presionado permite mover el mapa sin alterar
      // el perímetro que el operador ya viene dibujando.
      this.map.setOptions({ draggable: true, draggableCursor: 'crosshair', disableDoubleClickZoom: true });
    },

    addDraftPoint(event) {
      if (this.drawMode !== 'polygon') {
        return;
      }

      if (!this.draftPolygon) {
        this.draftPolygon = new this.google.maps.Polygon({
          paths: [event.latLng],
          strokeColor: this.selectedColor,
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: this.selectedColor,
          fillOpacity: 0.45,
          editable: false,
          clickable: false,
          map: this.map,
        });

        this.draftLine = new this.google.maps.Polyline({
          path: this.draftPolygon.getPath(),
          strokeColor: this.selectedColor,
          strokeOpacity: 1,
          strokeWeight: 3,
          map: this.map,
        });
      } else {
        this.draftPolygon.getPath().push(event.latLng);
      }

      this.draftMarkers.push(
        new this.google.maps.Marker({
          position: event.latLng,
          map: this.map,
          clickable: false,
          icon: {
            path: this.google.maps.SymbolPath.CIRCLE,
            scale: 4,
            fillColor: this.selectedColor,
            fillOpacity: 1,
            strokeColor: '#FFFFFF',
            strokeWeight: 1,
          },
        })
      );
      this.draftPointCount = this.draftPolygon.getPath().getLength();
    },

    undoDraftPoint() {
      if (!this.draftPolygon || this.draftPointCount === 0) {
        return;
      }

      this.draftPolygon.getPath().pop();
      const marker = this.draftMarkers.pop();

      if (marker) {
        marker.setMap(null);
      }

      this.draftPointCount = this.draftPolygon.getPath().getLength();
    },

    finishDrawing() {
      if (!this.draftPolygon || this.draftPointCount < 3) {
        this.$warning('Marque al menos tres puntos para cerrar el perímetro.');
        return;
      }

      const shape = this.draftPolygon;
      shape.type = 'polygon';
      shape.setOptions({ editable: true, clickable: true });

      this.draftPolygon = null;
      this.removeDraftLine();
      this.clearDraftMarkers();
      this.draftPointCount = 0;
      this.drawMode = null;
      this.map.setOptions({ draggable: true, draggableCursor: null, disableDoubleClickZoom: false });

      this.addShape(shape);
      this.watchShapePath(shape);
      this.syncShapeCoordinates(shape);
      this.removeDefaultMarker();
    },

    cancelDraft() {
      if (this.draftPolygon) {
        this.draftPolygon.setMap(null);
      }

      this.draftPolygon = null;
      this.removeDraftLine();
      this.clearDraftMarkers();
      this.draftPointCount = 0;
      this.drawMode = null;

      if (this.map) {
        this.map.setOptions({ draggable: true, draggableCursor: null, disableDoubleClickZoom: false });
      }
    },

    clearDraftMarkers() {
      this.draftMarkers.forEach((marker) => marker.setMap(null));
      this.draftMarkers = [];
    },

    removeDraftLine() {
      if (this.draftLine) {
        this.draftLine.setMap(null);
        this.draftLine = null;
      }
    },

    selectColor(color) {
      this.selectedColor = color;

      if (this.draftPolygon) {
        this.draftPolygon.setOptions({ strokeColor: color, fillColor: color });
      }
    },

    watchShapePath(shape) {
      ['insert_at', 'set_at', 'remove_at'].forEach((eventName) => {
        this.pathListeners.push(
          this.google.maps.event.addListener(shape.getPath(), eventName, () => {
            this.syncShapeCoordinates(shape);
          })
        );
      });
    },

    syncShapeCoordinates(shape, shouldReplaceHectares = true) {
      const coordinates = this.getCoordinates(shape);

      if (coordinates.length < 3) {
        return;
      }

      this.$emit(
        'shapeAdded',
        coordinates,
        this.calculateCenter(coordinates),
        this.calculateMeasurements(shape),
        shouldReplaceHectares
      );
    },

    calculateMeasurements(shape) {
      const path = shape.getPath().getArray();
      const spherical = this.google.maps.geometry && this.google.maps.geometry.spherical;

      if (!spherical || path.length < 3) {
        return null;
      }

      const closedPath = path.concat([path[0]]);
      const sides = path.map((point, index) => ({
        index: index + 1,
        meters: Number(spherical.computeDistanceBetween(point, closedPath[index + 1]).toFixed(2)),
      }));
      const perimeterMeters = sides.reduce((total, side) => total + side.meters, 0);
      const squareMeters = spherical.computeArea(path);

      return {
        hectares: Number((squareMeters / 10000).toFixed(4)),
        squareMeters: Number(squareMeters.toFixed(2)),
        perimeterMeters: Number(perimeterMeters.toFixed(2)),
        sides,
      };
    },

    deleteShapes() {
      this.cancelDraft();

      this.shapes.forEach((shape) => {
        shape.setMap(null);
        this.lastShape = shape;
      });

      this.shapes = [];
      this.selectedShape = null;
      this.$emit('shapeRemoved');
    },

    undo() {
      if (!this.lastShape) {
        return;
      }

      const shape = this.lastShape;
      shape.setMap(this.map);
      this.shapes.push(shape);
      this.lastShape = null;
      this.setSelection(shape);
      this.syncShapeCoordinates(shape, false);
    },

    setDefaultCoordinates() {
      if (this.defaultCoordinates && this.defaultCoordinates.length > 0 && this.location) {
        const shapeToAdd = new this.google.maps.Polygon({
          paths: this.defaultCoordinates,
          strokeColor: this.colors[0],
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: this.colors[0],
          fillOpacity: 0.45,
          editable: true,
          visible: true,
        });

        shapeToAdd.type = 'polygon';
        shapeToAdd.setMap(this.map);
        this.addShape(shapeToAdd);
        this.watchShapePath(shapeToAdd);
        this.syncShapeCoordinates(shapeToAdd, false);
        this.fitMapToCoordinates(this.defaultCoordinates);
      } else if (this.defaultLat && this.defaultLng) {
        this.defaultMarker = new this.google.maps.Marker({
          position: this.location,
          draggable: false,
          map: this.map,
          animation: this.google.maps.Animation.DROP,
          title: 'Ubicación del lote',
        });
      }
    },

    fitMapToCoordinates(coordinates) {
      if (!this.map || !coordinates || coordinates.length === 0) {
        return;
      }

      const bounds = new this.google.maps.LatLngBounds();
      coordinates.forEach((coordinate) => {
        const lat = Number(coordinate.lat);
        const lng = Number(coordinate.lng);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          bounds.extend({ lat, lng });
        }
      });

      if (!bounds.isEmpty()) {
        this.map.fitBounds(bounds, 56);
      }
    },

    removeDefaultMarker() {
      if (this.defaultMarker) {
        this.defaultMarker.setMap(null);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.map-canvas {
  height: 100vh;
}

.map-wrapper {
  position: relative;
}

.drawing-status {
  position: absolute;
  top: 16px;
  left: 16px;
  z-index: 4;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  padding: 10px 14px;
  color: #172b4d;
  background: #fff;
  border-left: 3px solid #2dce89;
  border-radius: 3px;
  box-shadow: 0 4px 12px rgba(50, 50, 93, 0.12);

  span {
    margin-top: 2px;
    font-size: 12px;
  }
}

.floating-panel {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 5;
  background-color: #fff;
  padding: 5px 15px 5px 15px;
  border: 1px solid #cacaca;
  text-align: center;
  font-family: 'Roboto', 'sans-serif';
  line-height: 30px;
}

.color-button {
  width: 20px;
  height: 20px;
  border-radius: 50px;
  font-size: 0;
  cursor: pointer;

  &.active {
    border: 2px solid #789789 !important;
  }
}
</style>
